import { Avatar } from "./Avatar";
import type { Attendee } from "../lib/attendees";

type AttendeeCardProps = {
  attendee: Attendee;
};

const normalizeLinkedinUrl = (url: string | null): string | null => {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(/^\/+/, "")}`;
};

export const AttendeeCard = ({ attendee }: AttendeeCardProps) => {
  const linkedinHref = normalizeLinkedinUrl(attendee.linkedinUrl);

  return (
    <article
      key={attendee.guestId}
      className="card-enter rounded-3xl border border-slate-800 bg-slate-900/70 p-8 shadow-2xl"
    >
      <div className="flex flex-col items-center text-center">
        <Avatar attendee={attendee} size={160} />
        <h2 className="mt-5 text-4xl font-semibold tracking-tight">
          {attendee.firstName || attendee.fullName || "Anonymous"}
        </h2>
        {linkedinHref && (
          <a
            href={linkedinHref}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-slate-800 px-3 py-1 text-xs font-medium text-slate-300 transition hover:border-sky-500/60 hover:text-sky-300"
          >
            <LinkedInIcon />
            LinkedIn
          </a>
        )}
      </div>

      <div className="mt-8 space-y-6">
        <Field label="Superpower" value={attendee.superpower} />
        <Field label="Biggest challenge" value={attendee.challenge} />
        <Field
          label="Newest, coolest, wildest solution"
          value={attendee.solution}
        />
      </div>
    </article>
  );
};

const LinkedInIcon = () => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className="h-3.5 w-3.5"
    fill="currentColor"
  >
    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.95v5.66H9.34V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.38-1.85 3.61 0 4.28 2.38 4.28 5.47v6.27zM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.55C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.72C24 .77 23.2 0 22.22 0z" />
  </svg>
);

const Field = ({ label, value }: { label: string; value: string | null }) => (
  <div>
    <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
      {label}
    </p>
    <p className="mt-1.5 text-base leading-relaxed text-slate-100">
      {value ?? (
        <span className="italic text-slate-500">— didn't say —</span>
      )}
    </p>
  </div>
);
